import { __assign, __extends, __read, __spreadArray } from "tslib";
import { Config, Logger, FetchTransport, LogLevel } from '@amplitude/analytics-core';
import { DEFAULT_PERFORMANCE_CONFIG, DEFAULT_SAMPLE_RATE, DEFAULT_SERVER_ZONE, DEFAULT_URL_CHANGE_POLLING_INTERVAL, MAX_INTERVAL, MIN_INTERVAL, UNMASK_TEXT_CLASS, } from '../constants';
import { SafeLoggerProvider } from '../logger';
import { validateUGCFilterRules } from '../helpers';
export var getDefaultConfig = function () { return ({
    flushMaxRetries: 2,
    logLevel: LogLevel.Warn,
    loggerProvider: new Logger(),
    transportProvider: new FetchTransport(),
}); };
var SessionReplayLocalConfig = /** @class */ (function (_super) {
    __extends(SessionReplayLocalConfig, _super);
    function SessionReplayLocalConfig(apiKey, options) {
        var _this = this;
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        var defaultConfig = getDefaultConfig();
        _this = _super.call(this, __assign(__assign({ transportProvider: defaultConfig.transportProvider, loggerProvider: new SafeLoggerProvider(options.loggerProvider || defaultConfig.loggerProvider) }, options), { apiKey: apiKey })) || this;
        _this.flushMaxRetries =
            options.flushMaxRetries !== undefined && options.flushMaxRetries <= defaultConfig.flushMaxRetries
                ? options.flushMaxRetries
                : defaultConfig.flushMaxRetries;
        _this.apiKey = apiKey;
        _this.sampleRate = options.sampleRate || DEFAULT_SAMPLE_RATE;
        _this.serverZone = options.serverZone || DEFAULT_SERVER_ZONE;
        _this.configServerUrl = options.configServerUrl;
        _this.trackServerUrl = options.trackServerUrl;
        _this.shouldInlineStylesheet = options.shouldInlineStylesheet;
        _this.version = options.version;
        _this.performanceConfig = options.performanceConfig || DEFAULT_PERFORMANCE_CONFIG;
        _this.storeType = (_a = options.storeType) !== null && _a !== void 0 ? _a : 'memory';
        _this.applyBackgroundColorToBlockedElements = (_b = options.applyBackgroundColorToBlockedElements) !== null && _b !== void 0 ? _b : false;
        _this.enableUrlChangePolling = (_c = options.enableUrlChangePolling) !== null && _c !== void 0 ? _c : false;
        _this.urlChangePollingInterval = (_d = options.urlChangePollingInterval) !== null && _d !== void 0 ? _d : DEFAULT_URL_CHANGE_POLLING_INTERVAL;
        _this.captureDocumentTitle = (_e = options.captureDocumentTitle) !== null && _e !== void 0 ? _e : false;
        if (options.fullSnapshotIntervalMs !== undefined) {
            _this.fullSnapshotIntervalMs = options.fullSnapshotIntervalMs;
        }
        // Auto-include .amp-unmask as a default unmaskSelector entry so it works
        // symmetrically with amp-mask/amp-block without requiring explicit config (SR-2945).
        _this.privacyConfig = __assign(__assign({}, ((_f = options.privacyConfig) !== null && _f !== void 0 ? _f : {})), { unmaskSelector: Array.from(new Set(__spreadArray([".".concat(UNMASK_TEXT_CLASS)], __read(((_h = (_g = options.privacyConfig) === null || _g === void 0 ? void 0 : _g.unmaskSelector) !== null && _h !== void 0 ? _h : [])), false))) });
        if (options.interactionConfig) {
            _this.interactionConfig = options.interactionConfig;
            // validate ugcFilterRules, throw error if invalid - throw error at the beginning of the config
            if (_this.interactionConfig.ugcFilterRules) {
                validateUGCFilterRules(_this.interactionConfig.ugcFilterRules);
            }
        }
        if (options.debugMode) {
            _this.debugMode = options.debugMode;
        }
        // Support both new useWebWorker and legacy experimental.useWebWorker for backwards compatibility
        if (options.useWebWorker !== undefined) {
            _this.useWebWorker = options.useWebWorker;
        }
        else {
            var legacyOptions = options;
            if (((_j = legacyOptions.experimental) === null || _j === void 0 ? void 0 : _j.useWebWorker) !== undefined) {
                _this.useWebWorker = legacyOptions.experimental.useWebWorker;
            }
        }
        _this.captureAdoptedStyleSheets = (_k = options.captureAdoptedStyleSheets) !== null && _k !== void 0 ? _k : true;
        if (options.crossOriginIframes) {
            _this.crossOriginIframes = options.crossOriginIframes;
        }
        if (options.flushIntervalConfig) {
            _this.flushIntervalConfig = sanitizeFlushIntervalConfig(options.flushIntervalConfig, _this.loggerProvider);
        }
        return _this;
    }
    return SessionReplayLocalConfig;
}(Config));
export { SessionReplayLocalConfig };
// 100ms floor avoids degenerate configs (0/negative) that would split on every event.
// Customers wanting fewer requests should be raising the value, not lowering it; the floor
// is just a defensive guard against typos and unsigned-int rollovers.
var MIN_FLUSH_INTERVAL_FLOOR_MS = 100;
function sanitizeFlushIntervalConfig(raw, loggerProvider) {
    var _a, _b;
    var sanitized = {};
    if (raw.minIntervalMs !== undefined) {
        if (!Number.isFinite(raw.minIntervalMs) || raw.minIntervalMs < MIN_FLUSH_INTERVAL_FLOOR_MS) {
            loggerProvider.warn("flushIntervalConfig.minIntervalMs ".concat(raw.minIntervalMs, " is below floor ").concat(MIN_FLUSH_INTERVAL_FLOOR_MS, "ms; clamping."));
            sanitized.minIntervalMs = MIN_FLUSH_INTERVAL_FLOOR_MS;
        }
        else {
            sanitized.minIntervalMs = raw.minIntervalMs;
        }
    }
    if (raw.maxIntervalMs !== undefined) {
        // Unlike min, `Infinity` is a meaningful value here: it means "no upper bound on interval
        // growth" (Math.min(Infinity, x) === x in BaseEventsStore.shouldSplitEventsList). Reject
        // only NaN and sub-floor values; pass Infinity through.
        if (Number.isNaN(raw.maxIntervalMs) || raw.maxIntervalMs < MIN_FLUSH_INTERVAL_FLOOR_MS) {
            loggerProvider.warn("flushIntervalConfig.maxIntervalMs ".concat(raw.maxIntervalMs, " is below floor ").concat(MIN_FLUSH_INTERVAL_FLOOR_MS, "ms; clamping."));
            sanitized.maxIntervalMs = MIN_FLUSH_INTERVAL_FLOOR_MS;
        }
        else {
            sanitized.maxIntervalMs = raw.maxIntervalMs;
        }
    }
    // Cross-validate against the SDK's effective defaults so that a partial config (only one of
    // {minIntervalMs, maxIntervalMs}) doesn't get silently clamped by the unspecified default.
    // Concrete failure mode without this: customer sets only `minIntervalMs: 30_000`, the store's
    // `maxInterval` falls back to `MAX_INTERVAL = 10_000`, and `shouldSplitEventsList` then
    // caps the effective interval at 10s — silently negating the customer's tune-up.
    // The user-supplied value always wins; we fill in the other side to match.
    if (sanitized.minIntervalMs !== undefined || sanitized.maxIntervalMs !== undefined) {
        var effectiveMin = (_a = sanitized.minIntervalMs) !== null && _a !== void 0 ? _a : MIN_INTERVAL;
        var effectiveMax = (_b = sanitized.maxIntervalMs) !== null && _b !== void 0 ? _b : MAX_INTERVAL;
        if (effectiveMax < effectiveMin) {
            if (sanitized.maxIntervalMs === undefined) {
                loggerProvider.warn("flushIntervalConfig.minIntervalMs (".concat(effectiveMin, ") exceeds the default maxIntervalMs (").concat(MAX_INTERVAL, "); raising max to match min."));
                sanitized.maxIntervalMs = effectiveMin;
            }
            else if (sanitized.minIntervalMs === undefined) {
                loggerProvider.warn("flushIntervalConfig.maxIntervalMs (".concat(effectiveMax, ") is below the default minIntervalMs (").concat(MIN_INTERVAL, "); lowering min to match max."));
                sanitized.minIntervalMs = effectiveMax;
            }
            else {
                loggerProvider.warn("flushIntervalConfig.maxIntervalMs (".concat(sanitized.maxIntervalMs, ") is less than minIntervalMs (").concat(sanitized.minIntervalMs, "); raising max to match min."));
                sanitized.maxIntervalMs = sanitized.minIntervalMs;
            }
        }
    }
    return sanitized;
}
//# sourceMappingURL=local-config.js.map